import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Reveal, Overline } from "@/components/Reveal";
import { CONTACT_EMAIL, SITE_URL } from "@/lib/site";
import { useLang } from "@/i18n";
import { MapPin, Mail, FileDown } from "lucide-react";

const schema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Farmsons",
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@type": "Organization",
    name: "Farmsons",
    email: CONTACT_EMAIL,
    address: { "@type": "PostalAddress", addressLocality: "Kolkata", addressRegion: "West Bengal", addressCountry: "IN" },
  },
};

export default function Contact() {
  const { t } = useLang();

  return (
    <>
      <SEO
        title="Contact & Request a Quote | Farmsons"
        description="Request a quote or partnership with Farmsons. Whether you're an international importer, orchard grower or supply partner, our Kolkata team will respond directly."
        keywords="Farmsons contact, request a quote produce, apple mango trading inquiry, partner with Farmsons"
        jsonLd={schema}
      />
      <section className="pt-36 md:pt-44 pb-24 md:pb-32 bg-[#ffffff] min-h-screen">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal><Overline>{t("contact.overline")}</Overline></Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 font-display font-extrabold tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl">{t("contact.title")}</h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#5f6b57] text-base md:text-lg leading-relaxed max-w-md">
                {t("contact.para")}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <address className="mt-10 space-y-4 not-italic">
                <div className="flex items-center gap-3 text-[#5f6b57]"><MapPin size={18} className="text-[#d3a53a]" /> {t("contact.address")}</div>
                <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-[#5f6b57] hover:text-[#d3a53a] transition-colors break-all"><Mail size={18} className="text-[#d3a53a]" /> {CONTACT_EMAIL}</a>
              </address>
              <div className="mt-8">
                <p className="text-sm text-[#5f6b57] mb-3">{t("contact.waPreface")}</p>
                <WhatsAppButton variant="inline" />
              </div>
              <div className="mt-8 border border-[#1d3a1f]/10 rounded-2xl p-6 bg-[#fdfbf5] card-soft">
                <h2 className="font-display font-bold text-lg text-[#1d3a1f]">{t("contact.profileTitle")}</h2>
                <p className="mt-2 text-sm text-[#5f6b57] leading-relaxed">{t("contact.profileDesc")}</p>
                <a href="/farmsons-company-profile.pdf" target="_blank" rel="noopener noreferrer" data-testid="contact-download-profile"
                  className="group mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors duration-200">
                  <FileDown size={16} /> {t("common.downloadProfile")}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><ContactForm /></Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
